﻿import {
    faCircle, faDrawPolygon as faPolygon, faMapMarker as faPoint, faQuestionCircle as faUnknown, faRoute as faLineString
} from '@fortawesome/pro-regular-svg-icons';

import Geometry, { Type } from 'ol/geom/Geometry';
import GeodesicCircle from './custom/GeodesicCircle';

type Subset<K, T extends K> = T;
export type GeometryType = Type;
export type GeometryDrawType = Subset<GeometryType, "Point" | "LineString" | "Polygon" | "Circle">;
export type GeometrySuperType = Subset<GeometryType, "Point" | "LineString" | "Polygon" | "Circle" | "GeometryCollection">;

class GeometryTypeInfo {

    public static geometryTypeLabels: Readonly<{ [key in GeometryType]: string }> = {   
        Point: "Point",
        LineString: "Line",
        LinearRing: "Ring",
        Polygon: "Polygon",
        MultiPoint: "Multi point",
        MultiLineString: "Multi line",
        MultiPolygon: "Multi polygon",
        GeometryCollection: "Collection",
        Circle: "Circle"
    }

    public static geometrySuperTypes: Readonly<{ [key in GeometryType]: GeometrySuperType }> = {
        Point: "Point",
        MultiPoint: "Point",
        LineString: "LineString",
        MultiLineString: "LineString",
        LinearRing: "Polygon",
        Polygon: "Polygon",
        MultiPolygon: "Polygon",
        Circle: "Circle",
        GeometryCollection: "GeometryCollection"
    }

    public static getSuperTypeForGeoJson(geojson: GeoJSON.GeoJSON | undefined): GeometrySuperType | undefined {
        switch (geojson?.type) {
            case "Point":
            case "MultiPoint":
                return "Point";
            case "LineString":
            case "MultiLineString":
                return "LineString";
            case "Polygon":
            case "MultiPolygon":
                return "Polygon";
            case "GeometryCollection":
                return "GeometryCollection";
            default:
                return undefined;
        }
    }

    public static getSuperTypeFor(type: GeometryType) {
        return this.geometrySuperTypes[type];
    }

    public static getTypesFor(superType: GeometryType) {
        const types: GeometryType[] = []
        for (var key in this.geometrySuperTypes) {
            const type = key as GeometryType;
            if (this.geometrySuperTypes[type] === superType)
                types.push(type);
        }

        return types;
    }

    public static isSuperTypeOf(type: GeometryType, superType: GeometryType) {
        return this.getSuperTypeFor(type) === superType;
    }

    public static getIconForType(type?: GeometryType) {
        if (type != null) {
            const superType = GeometryTypeInfo.getSuperTypeFor(type);
            switch (superType) {
                case "Point":
                    return faPoint;
                case "LineString":
                    return faLineString;
                case "Polygon":
                    return faPolygon;
                case "Circle":
                    return faCircle;
            }
        }
        return faUnknown;
    }

    public static getLabelForGeometry(geometry: Geometry | undefined) {
        if (geometry) {
            const type = GeodesicCircle.isGeodesicCircle(geometry) ?
                "Circle" : geometry.getType();
            return this.geometryTypeLabels[type];
        }
        return undefined;
    }
}

export default GeometryTypeInfo;